import {intersection, isEmpty} from 'lodash';
import { ServiceLocationType } from 'placement/const';
// Import from the domain files: the 'referral' barrel pulls in @stomp/stompjs, which Jest
// cannot load.
import {
    includedSocialOrGroupABA,
    isIncludedPractitioner,
    isTreatmentOfferedCareMedicated,
    isTreatmentOfferedPractitionerMedicated,
    Referral,
    ReferralAuthorizedHours,
    ReferralEstimatedHours,
    ServiceType
} from 'referral/domain/types';
import {
    isAssessmentWithConcurrentTreatment,
    isTreatmentHourRequired,
    isTreatmentOnly,
} from 'referral/domain/referral';
import {Offer} from './types';

export type RequiredHourFields =
    'directCareHours'
    | 'midLevelHours'
    | 'highLevelHours'
    | 'groupCareHours'
    | 'careMediatedMidHours'
    | 'careMediatedHighHours';

const FACTS = new Map<RequiredHourFields, ServiceType[]>([
    [
        'directCareHours', [
        ServiceType.Traditional_ABA,
        ServiceType.ABA_Services_Severe_Behavior,
    ]],
    [
        'midLevelHours', [
        ServiceType.Traditional_ABA,
        ServiceType.ABA_Services_Severe_Behavior,
        ServiceType.Parent_Led_ABA,
        ServiceType.ABA_Parent_Training,
        ServiceType.PLAY_Project,
        ServiceType.Group_ABA,
        ServiceType.Chat,
        ServiceType.Connect,
        ServiceType.Calibrate
    ]],
    [
        'highLevelHours', [
        ServiceType.Traditional_ABA,
        ServiceType.ABA_Services_Severe_Behavior,
        ServiceType.Parent_Led_ABA,
        ServiceType.ABA_Parent_Training,
        ServiceType.PLAY_Project,
        ServiceType.Group_ABA,
        ServiceType.Social_Skills_Group,
        ServiceType.Chat,
        ServiceType.Connect,
        ServiceType.Calibrate,
        ServiceType.RUBI,
        ServiceType.Compass_Behavioral,
    ]],
    [
        'groupCareHours', [
        ServiceType.Group_ABA,
        ServiceType.Social_Skills_Group,
    ]],
]);
export const getRequiredHourFields = (referral: Pick<Referral, 'serviceTypes'>,): Set<RequiredHourFields> => {
    const result = new Set<RequiredHourFields>();
    FACTS.forEach((serviceTypes, hourField) => {
        if (intersection(serviceTypes, referral.serviceTypes).length > 0) {
            result.add(hourField);
        }
    });
    return result;
};

export const maxHoursForOffer = (
    referral: Pick<Referral, 'placementType'> & ReferralEstimatedHours & ReferralAuthorizedHours,
): Record<RequiredHourFields, number> => {
    return (isTreatmentOnly(referral) || isAssessmentWithConcurrentTreatment(referral)) ? {
        'careMediatedMidHours': referral.careMediatedMidHours,
        'careMediatedHighHours': referral.careMediatedHighHours,
        'directCareHours': referral.practDirectOptimalHours,
        'midLevelHours': referral.practMidOptimalHours,
        'highLevelHours': referral.practHighOptimalHours,
        'groupCareHours': referral.practGroupOptimalHours,
    } : {
        'careMediatedMidHours': 0,
        'careMediatedHighHours': 0,
        'directCareHours': parseInt(referral.directCareHours, 10),
        'midLevelHours': parseInt(referral.midLevelHours, 10),
        'highLevelHours': parseInt(referral.highLevelHours, 10),
        'groupCareHours': parseInt(referral.groupCareHours, 10),
    };
};


export type OfferHoursDisplayCondition = {
    hasDirectHours: boolean;
    hasMidHours: boolean;
    hasHighHours: boolean;
    hasGroupHours: boolean;
    hasCareMediatedMidHours: boolean;
    hasCareMediatedHighHours: boolean;

    noPresetTreatmentOffered: boolean;
    isIncludeTreatmentOfferedCareMedicated: boolean;
    isTreatmentOfferedPractitioner: boolean;
    isTreatmentHoursRequired: boolean;
    isCareMedicated: boolean;
    showHoursInput: boolean;
};

export const offerHoursDisplayCondition = (treatmentOffered: string | null, noPresetTreatmentOffered: boolean, referral: Referral): OfferHoursDisplayCondition => {
    const dontHasCompassBehavioral = !referral.serviceTypes.includes(ServiceType.Compass_Behavioral);
    const isCareMedicated = isTreatmentOfferedCareMedicated(treatmentOffered);
    const isIncludeTreatmentOfferedCareMedicated = !isTreatmentOfferedPractitionerMedicated(treatmentOffered);
    const isTreatmentOfferedPractitioner = isIncludedPractitioner(treatmentOffered);
    const isTreatmentHoursRequired = isTreatmentHourRequired(referral);


    const isNotRubi = !(referral?.serviceTypes ?? []).includes(ServiceType.RUBI);
    const hasCareMediatedMidHours = isIncludeTreatmentOfferedCareMedicated && isNotRubi;
    const hasCareMediatedHighHours = isIncludeTreatmentOfferedCareMedicated;

    const hasDirectHours = dontHasCompassBehavioral && isTreatmentOfferedPractitioner;
    const hasMidHours = dontHasCompassBehavioral && isTreatmentOfferedPractitioner;
    const hasHighHours = isTreatmentOfferedPractitioner;
    const hasGroupHours = includedSocialOrGroupABA(referral.serviceTypes) && isTreatmentOfferedPractitioner;

    const showHoursInput = treatmentOffered != null && treatmentOffered !== '';

    return {
        hasDirectHours,
        hasMidHours,
        hasHighHours,
        hasGroupHours,
        isIncludeTreatmentOfferedCareMedicated,
        isTreatmentOfferedPractitioner,
        isTreatmentHoursRequired,
        isCareMedicated,
        showHoursInput,
        noPresetTreatmentOffered,
        hasCareMediatedMidHours,
        hasCareMediatedHighHours,
    } as OfferHoursDisplayCondition;
};

export const shouldRequireClinicInputBySelectedLocation= (isReferralRequireClinic: boolean, selectedServiceLocationTypes: Array<string|null>): boolean => {
    return !isReferralRequireClinic && selectedServiceLocationTypes.filter(lt => lt != null && lt === ServiceLocationType.ClinicCenter).length > 0;
}

// BHPNPP-2928
export type CaregiverLocationDisplay = {
    showAssessment: boolean;
    showMidLevel: boolean;
    showHighLevel: boolean;
    // A per-level line took the assessment line's place, so a value saved against the
    // assessment location is now orphaned. False when the assessment line is missing for some
    // other reason, such as a referral that allows no locations at all.
    assessmentReplaced: boolean;
};

// Which locations exist at each level. referralCaregiverLocations fills this from a referral.
// offerCaregiverLocations fills it from a saved offer.
export type CaregiverLocationInputs = {
    hasAssessment: boolean;
    hasMidLevel: boolean;
    hasHighLevel: boolean;
    // The assessment location is only a fallback, so a per-level location takes its place.
    assessmentIsFallback: boolean;
};

// Which Service Location lines the caregiver path shows.
// A caregiver-mediated offer names a location per supervision level. The assessment line
// drops out once a mid-level or high-level line shows, and stays when neither does.
// treatmentOffered must be resolved - the form value, or the referral's preset.
export const caregiverLocationDisplay = (
    inputs: CaregiverLocationInputs,
    treatmentOffered: string | null,
): CaregiverLocationDisplay => {
    const isPureCaregiver = isTreatmentOfferedCareMedicated(treatmentOffered);

    const showMidLevel = isPureCaregiver && inputs.hasMidLevel;
    const showHighLevel = isPureCaregiver && inputs.hasHighLevel;

    const assessmentReplaced = inputs.assessmentIsFallback && (showMidLevel || showHighLevel);
    const showAssessment = !isIncludedPractitioner(treatmentOffered)
        && inputs.hasAssessment
        && !assessmentReplaced;

    return {showAssessment, showMidLevel, showHighLevel, assessmentReplaced};
};

// Reader for the offer form: the locations the referral allows.
// RUBI has no mid-level supervision, so there is no mid-level location to name.
export const referralCaregiverLocations = (
    referral: Pick<Referral,
        'placementType' | 'serviceTypes' | 'serviceLocations' |
        'serviceLocationsMidLevel' | 'serviceLocationsHighLevel'>,
): CaregiverLocationInputs => ({
    hasAssessment: (referral.serviceLocations ?? []).length > 0,
    hasMidLevel: !(referral.serviceTypes ?? []).includes(ServiceType.RUBI)
        && (referral.serviceLocationsMidLevel ?? []).length > 0,
    hasHighLevel: (referral.serviceLocationsHighLevel ?? []).length > 0,
    assessmentIsFallback: isTreatmentOnly(referral),
});

// Reader for the read-only screens: the locations the offer saved.
// assessmentIsFallback is false because these screens show what was saved rather than
// re-applying the form's rules.
export const offerCaregiverLocations = (
    offer: Pick<Offer, 'serviceLocationType' |
        'serviceLocationMidLevelType' | 'serviceLocationHighLevelType'>,
): CaregiverLocationInputs => ({
    hasAssessment: !isEmpty(offer.serviceLocationType),
    hasMidLevel: !isEmpty(offer.serviceLocationMidLevelType),
    hasHighLevel: !isEmpty(offer.serviceLocationHighLevelType),
    assessmentIsFallback: false,
});
